import React, { useState, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import confetti from "canvas-confetti";

import LandingNavbar from "../../components/layout/LandingNavbar.tsx";
import StandardFooter from "../../components/Footer/StandardFooter";
import maxCoolLogo from "../../assets/images/max-cool-logo-nobg.png";
import professionalNetwork from "../../assets/images/professional-network.png";
import "./LandingPage.scss";
import { partners, Partner } from "../../assets/images/sponsors/partners.ts";
import { useTheme } from "../../context/ThemeContext";

const LandingPage: React.FC = () => {
    // Carousel setup for partners
    const [emblaRef] = useEmblaCarousel({ loop: true }, [
        Autoplay({ delay: 1500, stopOnInteraction: false, stopOnMouseEnter: true }),
    ]);

    // Theme setup
    const { isDarkMode, toggleDarkMode } = useTheme();
    const [initialDarkModeState, setInitialDarkModeState] = useState<boolean | null>(null);

    // Confetti setup
    const animationFrameId = useRef<number | null>(null);
    const confettiCanvasRef = useRef<HTMLCanvasElement | null>(null);
    let confettiInstance: ReturnType<typeof confetti.create> | null = null;

    // Add ref for hero section
    const heroSectionRef = useRef<HTMLElement>(null);
    const [isHeroVisible, setIsHeroVisible] = useState(true);

    // Function to scroll to features section
    const scrollToFeatures = () => {
        const featuresSection = document.getElementById("detailed-features");
        if (featuresSection) {
            featuresSection.scrollIntoView({ behavior: "smooth" });
        }
    };

    // Setup intersection observer for hero section
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                setIsHeroVisible(entry.isIntersecting);
            },
            {
                threshold: 0.1, // Trigger when at least 10% of the hero section is visible
            },
        );

        if (heroSectionRef.current) {
            observer.observe(heroSectionRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    // Setup confetti effect
    useEffect(() => {
        let confettiInstance: ReturnType<typeof confetti.create> | null = null;

        // Only setup confetti if hero section is visible
        if (isHeroVisible) {
            // Create and append canvas for confetti
            const canvasElement = document.createElement("canvas");
            canvasElement.id = "confetti-canvas";
            document.body.appendChild(canvasElement);
            confettiCanvasRef.current = canvasElement;

            if (confettiCanvasRef.current) {
                confettiInstance = confetti.create(confettiCanvasRef.current, {
                    resize: true,
                    useWorker: true,
                });
            }

            // Function to launch confetti particles
            const launchParticles = () => {
                if (confettiInstance && isHeroVisible) {
                    confettiInstance({
                        particleCount: 1,
                        angle: 270,
                        spread: 10,
                        startVelocity: 5,
                        origin: { y: -0.05, x: Math.random() },
                        gravity: 0.5,
                        drift: Math.random() * 0.5 - 0.25,
                        ticks: 200,
                        colors: ["#9f8f5e", "#BFAB78", "#E0D2A3", "#F0E68C", "#FAFAD2"],
                        scalar: Math.random() * 0.6 + 0.4,
                    });
                }
                if (isHeroVisible) {
                    animationFrameId.current = requestAnimationFrame(launchParticles);
                }
            };

            // Start the animation
            if (confettiInstance) {
                animationFrameId.current = requestAnimationFrame(launchParticles);
            }
        }

        // Cleanup function
        return () => {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }

            // Clean up confetti instance
            type ConfettiInstanceWithOptionalReset = ((options: confetti.Options) => void) & {
                reset?: () => void;
            };
            const instanceWithReset = confettiInstance as ConfettiInstanceWithOptionalReset | null;

            if (instanceWithReset && typeof instanceWithReset.reset === "function") {
                instanceWithReset.reset();
            }

            // Remove the canvas
            if (confettiCanvasRef.current) {
                document.body.removeChild(confettiCanvasRef.current);
                confettiCanvasRef.current = null;
            }
        };
    }, [isHeroVisible]); // Re-run when hero visibility changes

    // Force dark mode on mount and handle cleanup
    useEffect(() => {
        // Store initial state before we modify anything
        setInitialDarkModeState(isDarkMode);

        // Force dark mode
        if (!isDarkMode) {
            toggleDarkMode();
            // Ensure the theme is saved to localStorage
            localStorage.setItem("theme", "dark");
        }

        // Cleanup function
        return () => {
            // Only restore the original state if we're navigating away (not on refresh)
            if (initialDarkModeState !== null && !window.location.pathname.includes("/")) {
                localStorage.setItem("theme", initialDarkModeState ? "dark" : "light");
                if (isDarkMode !== initialDarkModeState) {
                    toggleDarkMode();
                }
            }
        };
    }, []); // Empty dependency array means this runs once on mount

    const getPartnerImageUrl = (imageName: string) => {
        return new URL(`../../assets/images/sponsors/${imageName}`, import.meta.url).href;
    };

    return (
        <div className="landing-page">
            {/* Include the NavBar component */}
            <LandingNavbar />

            {/* Hero Section */}
            <section className="hero-section" ref={heroSectionRef}>
                <div className="hero-content">
                    <div className="hero-video-placeholder">
                        <div className="hero-image-container">
                            <div className="glow-effect"></div>
                            <img src={maxCoolLogo} alt="MAX Aspire Logo" className="hero-image" />
                        </div>
                    </div>
                    <p className="slogan">
                        <i className="connect">Connect</i>,{" "}
                        <i className="collaborate">Collaborate</i>, and <i className="grow">Grow</i>{" "}
                        with your professional community
                    </p>
                    <div className="hero-buttons">
                        <a href="/auth" className="btn btn-primary">
                            Join the Community
                        </a>
                        <button className="btn btn-secondary" onClick={scrollToFeatures}>
                            Explore Features
                        </button>
                    </div>
                </div>
            </section>

            {/* Partners Section */}
            <section className="partners-section">
                <div className="embla" ref={emblaRef}>
                    <div className="embla__container">
                        {partners.map((partner: Partner, index: number) => (
                            <div className="embla__slide" key={index}>
                                <img
                                    src={getPartnerImageUrl(partner.img)}
                                    alt="Partner Logo"
                                    className="partner-logo"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Overview Section */}
            <section className="features-overview-section">
                <div className="features-overview-header">
                    <h2>Build your professional network with like-minded individuals</h2>
                </div>
                <div className="features-overview-media">
                    <div className="media-box">
                        <img
                            src={professionalNetwork}
                            alt="Professional Network"
                            className="features-overview-image"
                        />
                    </div>
                </div>
            </section>

            {/* Detailed Features Section(s) */}
            <section id="detailed-features" className="detailed-features-section">
                {/* Feature Block 1 */}
                <div className="feature-block">
                    <div className="feature-text-content">
                        <h3>Share your journey</h3>
                        <p>
                            Post updates, insights, and achievements to your professional network.
                            Get feedback, support, and recognition from your community.
                        </p>
                    </div>
                    <div className="feature-media-content">
                        <video
                            src="/videos/Aspire_Post_Demo.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="feature-video"
                        />
                    </div>
                </div>

                {/* Feature Block 2 (Layout potentially reversed via CSS) */}
                <div className="feature-block feature-block-reversed">
                    <div className="feature-media-content">
                        <video
                            src="/videos/Aspire_Comment_Demo.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="feature-video"
                        />
                    </div>
                    <div className="feature-text-content">
                        <h3>Engage with your community</h3>
                        <p>
                            Comment, like, and share posts from other professionals in your network.
                            Build relationships that can lead to mentorship, collaboration, and
                            career opportunities.
                        </p>
                        {/* <a href="/auth" className="feature-link">
                            Sign up now &gt;
                        </a> */}
                    </div>
                </div>

                {/* Feature Block 3 */}
                <div className="feature-block">
                    <div className="feature-text-content">
                        <h3>Personalized experience</h3>
                        <p>
                            Customize your profile, follow topics that interest you, and receive
                            personalized content and connection recommendations.
                        </p>
                        {/* <a href="/auth" className="feature-link">
                            Create your profile &gt;
                        </a> */}
                    </div>
                    <div className="feature-media-content">
                        <video
                            src="/videos/Aspire_Appearance_Demo.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="feature-video"
                        />
                    </div>
                </div>
            </section>

            {/* Call to Action Section */}
            <section className="cta-section">
                <div className="cta-content">
                    <h2>Ready to join the community?</h2>
                    <p>
                        Connect with professionals, share your journey, and grow your career with
                        MAX Aspire.
                    </p>
                    <a href="/auth" className="btn btn-primary">
                        Join the Community
                    </a>
                </div>
            </section>

            {/* Include the StandardFooter component */}
            <StandardFooter />
        </div>
    );
};

export default LandingPage;
